import React from "react";
import { motion } from "framer-motion";
import classNames from "classnames";
import type { Trigger } from "@/analytics";
import cls from "./styles.module.scss";

export type ButtonVariant =
  | "white"
  | "red"
  | "yellow"
  | "text"
  | "outlined"
  | "iconOnly"
  | "danger";

interface ButtonProps {
  label?: string | React.ReactNode;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  loading?: boolean;
  variant?: ButtonVariant | string;
  icon?: React.ReactNode;
  stopPropagation?: boolean;
  className?: string;
  animation?: boolean;
  ariaLabel?: string;
  trigger?: Trigger;
}

const Button: React.FC<ButtonProps> = ({
  label,
  onClick,
  disabled = false,
  loading = false,
  variant = "yellow",
  icon,
  stopPropagation,
  className = "",
  animation = false,
  ariaLabel,
  trigger,
}) => {
  const isDisabled = disabled || loading;
  const variantClass = cls[variant];

  const click = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) e.stopPropagation();
    onClick(e);
  };

  return (
    <motion.button
      type="button"
      aria-label={ariaLabel}
      data-trigger={trigger ?? ""}
      className={classNames(
        cls.button,
        variantClass,
        { [cls.disabled]: isDisabled },
        animation ? cls.animation : undefined,
        className
      )}
      onClick={click}
      disabled={isDisabled}
      whileHover={!isDisabled ? { scale: 1.02 } : {}}
      whileTap={!isDisabled ? { scale: 0.98 } : {}}
    >
      {loading ? (
        <div className={cls.spinner} aria-label="Loading" />
      ) : (
        <>
          {variant === "iconOnly"
            ? icon
            : icon && <div className={cls.icon}>{icon}</div>}
          {variant !== "iconOnly" && label && <div>{label}</div>}
        </>
      )}
    </motion.button>
  );
};

export default Button;
